import React, { useEffect, useRef, useState } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
  Animated,
} from "react-native";
import { useTranslation } from "react-i18next";
import Colors from "../../../Resources/Color";
import NoTranslateText from "../../Components/NoTranslateText";
import { useDevice } from "../../_layout";

/**
 * CookiePanel component
 * Slides in from bottom when visible, slides out when hidden
 * @param {boolean} visible - Controls panel visibility with animation
 * @param {function} onAccept - Callback when user accepts cookies
 * @param {function} onReject - Callback when user rejects cookies
 */
const CookiePanel = ({ visible, onAccept, onReject }) => {
  const slideAnim = useRef(new Animated.Value(200)).current; // Start off-screen
  const { t, i18n } = useTranslation();
  const { isMobile } = useDevice();
  const [rejectHovered, setRejectHovered] = useState(false);
  const [acceptHovered, setAcceptHovered] = useState(false);

  // Get localized cookie notice URL
  const getCookieNoticeUrl = () => {
    const language = i18n.language || "en";
    if (language.startsWith("es")) {
      return "https://efortcoach.com/es/cookie-notice";
    }
    return "https://efortcoach.com/cookie-notice";
  };

  useEffect(() => {
    if (visible) {
      // Slide in from bottom
      Animated.spring(slideAnim, {
        toValue: 0,
        useNativeDriver: true,
        tension: 65,
        friction: 10,
      }).start();
    } else {
      // Slide out to bottom
      Animated.timing(slideAnim, {
        toValue: 200,
        duration: 250,
        useNativeDriver: true,
      }).start();
    }
  }, [visible]);

  return (
    <Animated.View
      style={[
        styles.container,
        {
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      <NoTranslateText style={styles.text}>
        {t("cookiePanelText-string")}{" "}
        <Text
          style={styles.link}
          onPress={() => window.open(getCookieNoticeUrl(), "_blank")}
        >
          {t("cookiePanelLink-string")}
        </Text>{" "}
        {t("cookiePanelTextEnd-string")}
      </NoTranslateText>

      <View style={styles.buttonsRow}>
        <TouchableOpacity
          style={[
            styles.button,
            styles.primaryButton,
            acceptHovered && styles.primaryButtonHovered,
          ]}
          onPress={() => {
            if (onAccept) onAccept();
          }}
          onMouseEnter={() => !isMobile && setAcceptHovered(true)}
          onMouseLeave={() => setAcceptHovered(false)}
        >
          <Text style={styles.primaryText}>
            {t("cookiePanelAccept-string")}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.button, styles.secondaryButton]}
          onPress={() => {
            if (onReject) onReject();
          }}
          onMouseEnter={() => !isMobile && setRejectHovered(true)}
          onMouseLeave={() => setRejectHovered(false)}
        >
          <Text
            style={[
              styles.secondaryText,
              rejectHovered && styles.secondaryTextHovered,
            ]}
          >
            {t("cookiePanelReject-string")}
          </Text>
        </TouchableOpacity>
      </View>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: "fixed",
    bottom: 16,
    left: 8,
    right: 8,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 16,
    borderColor: "rgba(229, 229, 229, 1)",
    borderWidth: 1,
    backgroundColor: "rgba(239, 239, 239, 0.98)",
    flexDirection: "row",
    alignItems: "center",
    zIndex: 9999,
    maxWidth: 540,
    marginHorizontal: "auto",
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 4,
  },
  text: {
    flex: 1,
    color: "#000000ff",
    fontSize: 13,
  },
  link: {
    textDecorationLine: "underline",
    cursor: "pointer",
  },
  buttonsRow: {
    flexDirection: "row",
    marginLeft: 12,
    gap: 8,
  },
  button: {
    paddingHorizontal: 4,
    paddingVertical: 6,
    cursor: "pointer",
  },
  secondaryButton: {
    backgroundColor: "transparent",
  },
  primaryButton: {
    backgroundColor: "#000000",
    borderRadius: 10,
    paddingHorizontal: 12,
  },
  primaryButtonHovered: {
    backgroundColor: "#333333",
  },
  secondaryText: {
    color: "#000000ff",
    fontSize: 13,
    fontWeight: "600",
  },
  secondaryTextHovered: {
    color: "#666666ff",
  },
  primaryText: {
    color: "#FFFFFF",
    fontSize: 13,
    fontWeight: "600",
  },
});

export default CookiePanel;
